import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaUser, FaLock, FaCalendarAlt, FaChevronRight, FaInfoCircle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { tryNavigateFromProfileWizard } from '../utils/incompleteProfileLeaveGuard';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#e5e7eb', cardBg: '#ffffff' };

const Lead = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.98rem;
  line-height: 1.55;
  color: #475569;
`;

const Grid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  @media (min-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CardLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.15rem 1.2rem;
  border-radius: 14px;
  border: 1px solid ${BRAND.gray};
  background: ${BRAND.cardBg};
  text-decoration: none;
  color: #0f172a;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;

  &:hover {
    border-color: rgba(4, 54, 99, 0.28);
    box-shadow: 0 8px 28px rgba(4, 54, 99, 0.1);
    transform: translateY(-2px);
  }
`;

const CardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, rgba(4, 54, 99, 0.1), rgba(213, 43, 30, 0.08));
  color: ${BRAND.blue};
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-size: 1.02rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 0.28rem;
`;

const CardDesc = styled.div`
  font-size: 0.86rem;
  line-height: 1.45;
  color: #64748b;
`;

const ChevWrap = styled.span`
  align-self: center;
  color: #94a3b8;
  flex-shrink: 0;
`;

/** Landing page for player account settings. */
export default function PlayerSettingsHubPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();

  const cards = [
    {
      to: '/player/settings/profile',
      icon: FaUser,
      title: t('phase2.playerSettings.hubCards.profileTitle'),
      desc: t('phase2.playerSettings.hubCards.profileDesc'),
    },
    {
      to: '/player/settings/availability',
      icon: FaCalendarAlt,
      title: t('phase2.playerSettings.hubCards.availabilityTitle'),
      desc: t('phase2.playerSettings.hubCards.availabilityDesc'),
    },
    {
      to: '/player/league-info',
      icon: FaInfoCircle,
      title: t('phase2.playerSettings.hubCards.leagueInfoTitle'),
      desc: t('phase2.playerSettings.hubCards.leagueInfoDesc'),
    },
    {
      to: '/player/settings/security',
      icon: FaLock,
      title: t('phase2.playerSettings.hubCards.securityTitle'),
      desc: t('phase2.playerSettings.hubCards.securityDesc'),
    },
  ];

  return (
    <div>
      <Lead>{t('phase2.playerSettings.hubLead')}</Lead>
      <Grid>
        {cards.map(({ to, icon: Icon, title, desc }) => (
          <CardLink
            key={to}
            to={to}
            onClick={(e) => {
              if (!tryNavigateFromProfileWizard(user, location.pathname, to)) e.preventDefault();
            }}
          >
            <CardIcon>
              <Icon size={20} aria-hidden />
            </CardIcon>
            <CardBody>
              <CardTitle>{title}</CardTitle>
              <CardDesc>{desc}</CardDesc>
            </CardBody>
            <ChevWrap>
              <FaChevronRight size={14} aria-hidden />
            </ChevWrap>
          </CardLink>
        ))}
      </Grid>
    </div>
  );
}
